import { Building2, ClipboardList, FileText, House, LayoutGrid, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { KPICard } from '../../components/Dashboard/KPICard';
import { BarChart } from '../../components/Dashboard/BarChart';
import { DoughnutChart } from '../../components/Dashboard/DoughnutChart';
import { TrafficList } from '../../components/Dashboard/TrafficList';
import { ActivityFeed } from '../../components/Dashboard/ActivityFeed';
import { ContactsList } from '../../components/Dashboard/ContactsList';
import { UseCaseMatrixCard } from '../../components/Dashboard';
import { CadrageStatusCard } from '../../components/Dashboard/CadrageStatusCard';
import { ForgeAppsMetricsCard } from '../../components/Dashboard/ForgeAppsMetricsCard';
import { TimelineVelocityChart } from '../../components/Dashboard/TimelineVelocityChart';
import { ProjectsWidget } from '../../components/Dashboard/ProjectsWidget';
import { ReviewRequestsWidget } from '../../components/Dashboard/ReviewRequestsWidget';
import { AnalysisRunsWidget } from '../../components/Dashboard/AnalysisRunsWidget';
import { PortfolioKpisWidget, PortfolioMinimapWidget } from '../../components/Dashboard/PortfolioWidgets';
import { DomainCard } from '../../components/Common/DomainCard';
import { Button } from '../../components/Common/button';
import { Can } from '../../components/Auth/Can';
import { PageTitle } from '../../components/Layout/PageTitle';
import { domainService } from '../../services/domainService';
import { useCaseService } from '../../services/useCaseService';
import cadrageService from '../../services/cadrageService';
import organizationService from '../../services/organizationService';
import dashboardService from '../../services/dashboardService';
import { useAuthStore } from '../../stores/authStore';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useProfileUi } from '../../hooks/useProfileUi';
import { useOrgModules } from '../../hooks/useOrgModules';
import { useAppName } from '../../hooks/useAppName';
import { Domain, DomainWithDetails, UseCaseSummary, UseCaseDetail, CadrageReadWithTeam, User } from '../../types';
import { DashboardStats } from '../../types/dashboard';
import { DASHBOARD_WIDGETS, type ControlledResource, type DashboardWidgetId } from '../../types/profiles';
import { useTranslation } from 'react-i18next';
import {
  countCreatedThisWeek,
  buildUseCaseStats,
  buildBarChartData,
  buildCadrageStats,
  buildDoughnutChartData,
  buildLatestUseCasesData,
  buildActivities,
  buildContacts,
  buildLastModifiedDomains,
  buildCadrageStatusBreakdown,
} from './Dashboard.helpers';

/**
 * A dashboard widget. `render` receives the wrapper class of its slot (grid
 * span in the default layout, nothing in a custom order) and returns null when
 * the widget's own gates (existing permission checks) hide it.
 */
interface DashboardWidget {
  id: DashboardWidgetId;
  resource: ControlledResource;
  render: (slotClassName?: string) => React.ReactNode;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { canRead } = usePermissions();
  const { moduleEnabled } = useOrgModules();
  const appName = useAppName();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const { dashboardWidgets, isWidgetHiddenByExperience } = useProfileUi();
  const [allDomains, setAllDomains] = useState<Domain[]>([]);
  const [domains, setDomains] = useState<DomainWithDetails[]>([]);
  const [useCasesList, setUseCasesList] = useState<UseCaseSummary[]>([]);
  const [useCases, setUseCases] = useState<UseCaseDetail[]>([]);
  const [cadrages, setCadrages] = useState<CadrageReadWithTeam[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<User[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasNoDomains, setHasNoDomains] = useState(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setHasNoDomains(false);

        // Load domains first - this determines what the user can see
        let domainsData: DomainWithDetails[] = [];
        if (canRead('domains')) {
          try {
            domainsData = await domainService.getUserDomains();
          } catch (error) {
            console.error('Failed to load domains', error);
            domainsData = [];
          }
        }

        // If user has no domains (and can read them), show empty state
        if (canRead('domains') && domainsData.length === 0) {
          setHasNoDomains(true);
          setAllDomains([]);
          setDomains([]);
          setUseCasesList([]);
          setUseCases([]);
          setCadrages([]);
          setLoading(false);

          // Still try to load organization members
          if (user?.organization_id && canRead('my-organization')) {
            try {
              const members = await organizationService.getOrganizationUsers(user.organization_id);
              setOrganizationMembers(members);
            } catch {
              setOrganizationMembers([]);
            }
          }
          return;
        }

        // Load use cases and cadrages - these are now filtered by backend
        let useCasesData: UseCaseSummary[] = [];
        let cadragesData: CadrageReadWithTeam[] = [];

        if (canRead('usecases')) {
          try {
            useCasesData = await useCaseService.list({ limit: 500 });
          } catch (error) {
            console.error('Failed to load use cases', error);
            useCasesData = [];
          }
        }

        if (canRead('cadrages')) {
          try {
            cadragesData = await cadrageService.getCadrages();
          } catch (error) {
            console.error('Failed to load cadrages', error);
            cadragesData = [];
          }
        }

        // Get detailed use cases for activities (with individual error handling)
        const useCasesWithDetails = canRead('usecases')
          ? await Promise.all(
              useCasesData.slice(0, 10).map(async (useCase: UseCaseSummary) => {
                try {
                  return await useCaseService.get(useCase.id);
                } catch (error) {
                  console.error(`Failed to load details for use case ${useCase.id}`, error);
                  return useCase as UseCaseDetail;
                }
              })
            )
          : [];

        setAllDomains(domainsData);
        setUseCasesList(useCasesData);

        let domainsWithDetails: DomainWithDetails[] = [];
        if (canRead('domains') && domainsData.length > 0) {
          const sortedByUpdated = [...domainsData].sort((a, b) => {
            const aDate = a.updated_at ? new Date(a.updated_at).getTime() : 0;
            const bDate = b.updated_at ? new Date(b.updated_at).getTime() : 0;
            return bDate - aDate;
          });
          const domainsToEnrich = sortedByUpdated.slice(0, 2);
          domainsWithDetails = await Promise.all(
            domainsToEnrich.map(async (domain: Domain) => {
              try {
                const [detailedDomain, stats] = await Promise.all([
                  domainService.getDomainDetails(domain.id),
                  domainService.getDomainStats(domain.id),
                ]);
                return {
                  ...domain,
                  total_members: stats?.total_members ?? 0,
                  total_cadrages: stats?.total_cadrages ?? 0,
                  total_use_cases: stats?.total_use_cases ?? 0,
                  members: detailedDomain.members || [],
                  cadrages: detailedDomain.cadrages || []
                };
              } catch (error) {
                console.error(`Failed to load details for domain ${domain.id}`, error);
                return {
                  ...domain,
                  total_members: 0,
                  total_cadrages: 0,
                  total_use_cases: 0,
                  members: [],
                  cadrages: []
                };
              }
            })
          );
        }

        // Load organization members if user has organization
        let members: User[] = [];
        if (user?.organization_id && canRead('my-organization')) {
          try {
            members = await organizationService.getOrganizationUsers(user.organization_id);
          } catch (error) {
            console.error('Failed to load organization members', error);
          }
        }

        setDomains(domainsWithDetails);
        setUseCases(useCasesWithDetails);
        setCadrages(cadragesData);
        setOrganizationMembers(members);

        // Load dashboard statistics
        try {
          const stats = await dashboardService.getDashboardStats();
          setDashboardStats(stats);
        } catch (error) {
          console.error('Failed to load dashboard statistics', error);
        }
      } catch (error) {
        console.error('Failed to load dashboard data', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.organization_id]);

  // Calculate KPI metrics from full lists (not sliced display data)
  const totalDomains = allDomains.length;
  const totalUseCases = useCasesList.length;
  const totalCadrages = cadrages.length;

  const domainsThisWeek = countCreatedThisWeek(allDomains, (d) => d.created_at);
  const useCasesThisWeek = countCreatedThisWeek(useCasesList, (uc) => uc.created_at);
  const cadragesThisWeek = countCreatedThisWeek(cadrages, (c) => c.created_at);

  const useCaseStats = buildUseCaseStats(useCasesList);
  const barChartData = buildBarChartData(useCaseStats);

  const cadrageStats = buildCadrageStats(cadrages);
  const doughnutChartData = buildDoughnutChartData(cadrageStats);

  const latestUseCasesData = buildLatestUseCasesData(useCasesList);
  const activities = buildActivities(useCases, cadrages);
  const contacts = buildContacts(organizationMembers);
  const lastModifiedDomains = buildLastModifiedDomains(domains);

  const pageTitle = (
    <PageTitle
      icon={<House />}
      title={t('sidebar.dashboard')}
      subtitle={t('dashboard.pageSubtitle')}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        {pageTitle}
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {t('dashboard.loadingDashboard', 'Loading dashboard...')}
          </p>
        </div>
      </div>
    );
  }

  // Empty state for users with no domains (only shown if they can read domains)
  if (hasNoDomains) {
    return (
      <div className="space-y-6">
        {pageTitle}
        {/* Welcome message for new users */}
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <LayoutGrid className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {t('dashboard.noDomains.title', 'Welcome to {{app}}!', { app: appName })}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t('dashboard.noDomains.description', "You don't have access to any domains yet. Contact your organization administrator to get access to domains, or create a new one if you have the permissions.")}
          </p>
          <Can resource="domains" intent="write">
            <Button
              onClick={() => navigate('/domains/new')}
              className="flex items-center gap-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              {t('dashboard.noDomains.viewDomains', 'View Domains')}
            </Button>
          </Can>
        </div>

        {/* Organization Members - show even without domains */}
        <Can resource="my-organization">
          {organizationMembers.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <ContactsList
                  title={t('dashboard.organizationMembers')}
                  contacts={buildContacts(organizationMembers)}
                />
              </div>
            </div>
          )}
        </Can>
      </div>
    );
  }

  // Widget registry (plan docs/planning/olise-integration-plan.md §9.5). Each
  // entry keeps the gates the dashboard always had; `resource` is the catalog
  // permission used to filter a profile's custom dashboard. Registered project
  // widgets: my-projects, gallery-latest (L1), analysis-runs and
  // review-requests (L3), projects-kpis and portfolio-minimap (L4). Every
  // catalog widget is now registered.
  const widgetRegistry: Partial<Record<DashboardWidgetId, DashboardWidget>> = {
    kpis: {
      id: 'kpis',
      resource: DASHBOARD_WIDGETS.kpis,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-tour="kpi-cards">
          <Can resource="domains">
            <div data-tour="kpi-domains" className="h-full">
              <KPICard
                title={t('dashboard.totalDomains')}
                value={totalDomains}
                change={{ value: domainsThisWeek, isPositive: true }}
                icon={LayoutGrid}
                className="h-full"
              />
            </div>
          </Can>
          <Can resource="usecases">
            <div data-tour="kpi-usecases" className="h-full">
              <KPICard
                title={t('dashboard.totalUseCases')}
                value={totalUseCases}
                change={{ value: useCasesThisWeek, isPositive: true }}
                icon={ClipboardList}
                className="h-full"
              />
            </div>
          </Can>
          <Can resource="cadrages">
            <div data-tour="kpi-cadrages" className="h-full">
              <KPICard
                title={t('dashboard.totalCadrages')}
                value={totalCadrages}
                change={{ value: cadragesThisWeek, isPositive: true }}
                icon={FileText}
                className="h-full"
              />
            </div>
          </Can>
          {/* Actions Rapides Card */}
          {(canRead('usecases') || canRead('cadrages') || canRead('my-organization')) && (
            <div className="bg-card border border-border rounded-xl p-6 h-full flex flex-col justify-center" data-tour="quick-actions">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Can resource="usecases" intent="write">
                    <Button
                      onClick={() => navigate('/cas-usage/new')}
                      className="flex items-center gap-1.5 flex-1 justify-center"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 flex-shrink-0" />
                      {t('dashboard.actions.addUseCase')}
                    </Button>
                  </Can>
                  <Can resource="cadrages" intent="write">
                    <Button
                      onClick={() => navigate('/cadrages/new')}
                      className="flex items-center gap-1.5 flex-1 justify-center"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 flex-shrink-0" />
                      {t('dashboard.actions.addCadrage')}
                    </Button>
                  </Can>
                </div>
                <Can resource="my-organization">
                  <Button
                    onClick={() => navigate('/my-organization')}
                    className="flex items-center gap-2 w-full justify-start"
                    variant="outline"
                    size="sm"
                  >
                    <Building2 className="h-4 w-4" />
                    {t('dashboard.actions.manageOrganisation')}
                  </Button>
                </Can>
              </div>
            </div>
          )}
        </div>
      ),
    },
    'timeline-velocity': {
      id: 'timeline-velocity',
      resource: DASHBOARD_WIDGETS['timeline-velocity'],
      render: () =>
        dashboardStats && (canRead('usecases') || canRead('cadrages')) ? (
          <TimelineVelocityChart
            data={dashboardStats.timeline_velocity}
            overrides={{
              totalUseCases: totalUseCases,
              totalCadrages: totalCadrages,
            }}
          />
        ) : null,
    },
    domains: {
      id: 'domains',
      resource: DASHBOARD_WIDGETS.domains,
      render: () =>
        canRead('domains') && lastModifiedDomains.length > 0 ? (
          <div className="space-y-4" data-tour="recent-domains">
            <h2 className="text-lg font-semibold text-foreground">{t('dashboard.recentDomains')}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {lastModifiedDomains.map((domain) => (
                <DomainCard
                  key={domain.id}
                  domain={domain}
                  onClick={() => navigate(`/domains/${domain.id}`)}
                />
              ))}
            </div>
          </div>
        ) : null,
    },
    'usecase-matrix': {
      id: 'usecase-matrix',
      resource: DASHBOARD_WIDGETS['usecase-matrix'],
      // Use Case Matrix - Prioritization Chart (autonomous component)
      render: () =>
        canRead('usecases') ? (
          <div data-tour="use-case-matrix">
            <UseCaseMatrixCard title={t('dashboard.useCaseMatrixTitle')} />
          </div>
        ) : null,
    },
    charts: {
      id: 'charts',
      resource: DASHBOARD_WIDGETS.charts,
      render: () =>
        canRead('usecases') || canRead('cadrages') ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Can resource="usecases">
              <div>
                <BarChart data={barChartData} title={t('dashboard.useCasesByStatus')} />
              </div>
            </Can>
            <Can resource="cadrages">
              <div>
                <DoughnutChart data={doughnutChartData} title={t('dashboard.cadragesByType')} />
              </div>
            </Can>
          </div>
        ) : null,
    },
    traffic: {
      id: 'traffic',
      resource: DASHBOARD_WIDGETS.traffic,
      // Latest use cases: keeps its historical use-case gate on top of the catalog resource.
      render: (slotClassName) =>
        canRead('usecases') ? (
          <div className={slotClassName}>
            <TrafficList title={t('dashboard.latestUseCases')} items={latestUseCasesData} />
          </div>
        ) : null,
    },
    activity: {
      id: 'activity',
      resource: DASHBOARD_WIDGETS.activity,
      render: (slotClassName) =>
        canRead('usecases') || canRead('cadrages') ? (
          <div className={slotClassName} data-tour="activity-feed">
            <ActivityFeed title={t('dashboard.activities')} activities={activities} />
          </div>
        ) : null,
    },
    contacts: {
      id: 'contacts',
      resource: DASHBOARD_WIDGETS.contacts,
      render: (slotClassName) =>
        canRead('my-organization') ? (
          <div className={slotClassName} data-tour="org-members">
            <ContactsList title={t('dashboard.organizationMembers')} contacts={contacts} />
          </div>
        ) : null,
    },
    'cadrage-status': {
      id: 'cadrage-status',
      resource: DASHBOARD_WIDGETS['cadrage-status'],
      // Built from the actual cadrages list for correct totals.
      render: (slotClassName) =>
        canRead('cadrages') ? (
          <div className={slotClassName} data-tour="cadrage-status">
            <CadrageStatusCard data={buildCadrageStatusBreakdown(cadrages)} />
          </div>
        ) : null,
    },
    'forge-apps': {
      id: 'forge-apps',
      resource: DASHBOARD_WIDGETS['forge-apps'],
      // Forges read and the Forge API switch on; the card handles "no Forge client".
      render: (slotClassName) =>
        canRead('forges') && forgeApiEnabled ? (
          <div className={slotClassName}>
            <ForgeAppsMetricsCard />
          </div>
        ) : null,
    },
    // Projects widgets (lot L1): custom dashboards only, behind the org modules.
    'my-projects': {
      id: 'my-projects',
      resource: DASHBOARD_WIDGETS['my-projects'],
      render: (slotClassName) =>
        canRead('projects') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <ProjectsWidget variant="mine" />
          </div>
        ) : null,
    },
    'gallery-latest': {
      id: 'gallery-latest',
      resource: DASHBOARD_WIDGETS['gallery-latest'],
      // The gallery lists projects: it needs the projects module as well.
      render: (slotClassName) =>
        canRead('gallery') && moduleEnabled('gallery') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <ProjectsWidget variant="gallery" />
          </div>
        ) : null,
    },
    'analysis-runs': {
      id: 'analysis-runs',
      resource: DASHBOARD_WIDGETS['analysis-runs'],
      render: (slotClassName) =>
        canRead('projects') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <AnalysisRunsWidget />
          </div>
        ) : null,
    },
    'review-requests': {
      id: 'review-requests',
      resource: DASHBOARD_WIDGETS['review-requests'],
      render: (slotClassName) =>
        canRead('projects') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <ReviewRequestsWidget />
          </div>
        ) : null,
    },
    // Portfolio widgets (lot L4): the portfolio reads projects, so both modules must be on.
    'projects-kpis': {
      id: 'projects-kpis',
      resource: DASHBOARD_WIDGETS['projects-kpis'],
      render: (slotClassName) =>
        canRead('portfolio') && moduleEnabled('portfolio') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <PortfolioKpisWidget />
          </div>
        ) : null,
    },
    'portfolio-minimap': {
      id: 'portfolio-minimap',
      resource: DASHBOARD_WIDGETS['portfolio-minimap'],
      render: (slotClassName) =>
        canRead('portfolio') && moduleEnabled('portfolio') && moduleEnabled('projects') ? (
          <div className={slotClassName}>
            <PortfolioMinimapWidget />
          </div>
        ) : null,
    },
  };

  const renderWidget = (id: DashboardWidgetId, slotClassName?: string): React.ReactNode => {
    if (isWidgetHiddenByExperience(id)) return null;
    const widget = widgetRegistry[id];
    return widget ? widget.render(slotClassName) : null;
  };

  // Custom dashboard from the profile: visible widgets, in the profile's order,
  // full width, skipping unreadable or not-yet-implemented ones.
  if (dashboardWidgets) {
    return (
      <div className="space-y-6">
        {pageTitle}
        {dashboardWidgets
          .filter((w) => w.visible !== false)
          .filter((w) => widgetRegistry[w.id] && canRead(DASHBOARD_WIDGETS[w.id]))
          .map((w) => (
            <React.Fragment key={w.id}>{renderWidget(w.id)}</React.Fragment>
          ))}
      </div>
    );
  }

  // Default dashboard: historical layout and grouping. Only the experience
  // (Olisé hides use-case widgets) can remove blocks here.
  const showCharts = !isWidgetHiddenByExperience('charts');

  return (
    <div className="space-y-6">
      {pageTitle}
      {/* KPI Cards */}
      {renderWidget('kpis')}

      {/* Timeline/Velocity Chart */}
      {renderWidget('timeline-velocity')}

      {/* Last Modified Domains */}
      {renderWidget('domains')}

      {/* Use Case Matrix - Prioritization Chart */}
      {renderWidget('usecase-matrix')}

      {/* Charts Section - Grouped together */}
      {(canRead('usecases') || canRead('cadrages') || canRead('my-organization')) && (
        <div className="space-y-6" data-tour="charts-section">
          {/* Main Analytics Row */}
          {showCharts && renderWidget('charts')}

          {/* Latest Use Cases, Activities, and Organization Members Row */}
          <div className="grid grid-cols-1 lg:grid-cols-8 gap-6">
            {renderWidget('traffic', 'lg:col-span-3')}
            {renderWidget('activity', 'lg:col-span-3')}
            {renderWidget('contacts', 'lg:col-span-2')}
          </div>
        </div>
      )}

      {/* New Dashboard Statistics Section */}
      {(canRead('cadrages') || canRead('forges')) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {renderWidget('cadrage-status', 'lg:col-span-2')}
          {renderWidget('forge-apps')}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
